import psl from 'psl';
import { match } from 'path-to-regexp';
import { CORE_APP_URL, NON_CORE_APP_URL, REGIONAL_URLS, LITE_APP_URL } from '../../../config';
import { IAccountDetails, IUser } from '../../../slices/users/user';
import { isIncrowdClient } from '../../../utils/clients';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { Regions } from '../../common/domain/accountRegions';
import { UserTypes } from '../../common/domain/userTypes';
import { isAccountAdmin, isPlatformAdmin } from '../../../utils/user';
import { prospectOnlyPaths } from './validateRequestedPathForProspect';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';

const newPathsForEnterpriseUsers = isFeatureEnabled(Features.BillingUserFacing) ? ['/settings/plan-billing'] : [];

export const COPY_TEMPLATE_BASE_URL = '/copy-template';
export const allowedDomains: ReadonlyArray<string> = ['sightx.io'];
export const alwaysAllowedPaths: ReadonlyArray<string> = ['/logout', '/login'];
export const allowedPathsForUsers: ReadonlyArray<string> = [...alwaysAllowedPaths, COPY_TEMPLATE_BASE_URL, '/settings/profile', '/settings/subscription', '/settings/subscription/compare', '/settings/subscription/edit', '/settings/users', '/settings/subscription/upgrade-to-pro', ...newPathsForEnterpriseUsers];
export const allowedPathsForAccountAdmin: ReadonlyArray<string> = [...alwaysAllowedPaths, ...allowedPathsForUsers, '/settings/billing', ...newPathsForEnterpriseUsers];
export const allowedPathsForProspects: ReadonlyArray<string> = [...alwaysAllowedPaths, '/signup/pro/subscription', '/signup/pro/payment/:paymentId'];

export const getUrlToRedirect = (user: IUser, baseUrl = '', path = ''): URL => {
  const { userType, account, hasPayment, accessType } = user;
  const noneCoreUrl = validateURL(`${NON_CORE_APP_URL}${path}`) || new URL(NON_CORE_APP_URL);
  if (userType === UserTypes.Prospect) {
    if (hasPayment || accessType === AccessTypes.Lite) {
      return new URL(`${NON_CORE_APP_URL}/signup/account-creation`);
    }
    if (isPathIncluded(noneCoreUrl.pathname, allowedPathsForProspects)) {
      return noneCoreUrl;
    }
    return new URL(`${NON_CORE_APP_URL}/signup/pro/subscription`);
  }

  const redirectUrlOrNull = validateURL(baseUrl);

  if (!redirectUrlOrNull) {
    const isValidCopyTemplateAccessType = accessType && [AccessTypes.Lite, AccessTypes.Professional].includes(accessType);
    if (isValidCopyTemplateAccessType && path.startsWith(COPY_TEMPLATE_BASE_URL)) {
      return new URL(`${NON_CORE_APP_URL}${path}`);
    }
    if (isPlatformAdmin(userType)) {
      if (!path || isPathIncluded(path, prospectOnlyPaths)) {
        return new URL(`${NON_CORE_APP_URL}/accounts`);
      }
      return new URL(`${NON_CORE_APP_URL}${path}`);
    }
    if (isAccountAdmin(userType)) {
      if (!isPathIncluded(path, allowedPathsForAccountAdmin)) {
        return new URL(`${NON_CORE_APP_URL}/settings/profile`);
      }
      return noneCoreUrl;
    }
    if (isPathIncluded(path, allowedPathsForUsers)) {
      return noneCoreUrl;
    }
    return new URL(`${NON_CORE_APP_URL}/settings/profile`);
  }

  const coreUrl = getDefaultCoreUrl(account);
  if (!isPlatformAdmin(userType)) {
    return replaceDomain(redirectUrlOrNull, coreUrl);
  }

  if (!isValidDomain(redirectUrlOrNull.hostname)) {
    return replaceDomain(redirectUrlOrNull, coreUrl);
  }

  return redirectUrlOrNull;
};

export const getDefaultCoreUrl = (account?: IAccountDetails): string => {
  const { region = '', accessType = '' } = account || {};
  if (isIncrowdClient()) {
    return CORE_APP_URL;
  }
  if (accessType === AccessTypes.Lite) {
    return LITE_APP_URL;
  }
  switch (region.toUpperCase()) {
  case Regions.Australia:
    return REGIONAL_URLS.AUSTRALIA;
  case Regions.EuropeUnion:
    return REGIONAL_URLS.EUROPE_UNION;
  default:
    return CORE_APP_URL;
  }
};

const validateURL = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch (err) {
    return null;
  }
};

const isValidDomain = (hostname: string): boolean => {
  const parserHostOrError = psl.parse(hostname);
  if (parserHostOrError.error) {
    return false;
  }
  const { domain } = parserHostOrError;
  if (!domain) {
    return false;
  }
  return allowedDomains.some(allowedDomain => (domain.toLowerCase()) === allowedDomain);
};

const replaceDomain = (url: URL, domain: string): URL => {
  const stringURL = url.toString();
  const splitURL = stringURL.split(url.host);
  splitURL.shift();
  return new URL(domain + splitURL.join());
};

const isPathIncluded = (path: string, allowedPaths: ReadonlyArray<string>): boolean => {
  return allowedPaths.some(candidate => match(candidate)(path));
};

export const isNoneCoreUrl = (url: URL): boolean => {
  return `${url.protocol}//${url.host}` === NON_CORE_APP_URL;
};
